/* PTPage — Personal Trainer (Nova by default) with workout generator + visuals.
 * Renders under Drömmar -> PT tab. */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dumbbell, Flame, Sparkles, Trash2, CheckCircle2, Loader2, Image as ImgIcon,
  Heart, Trophy, X, Settings, Play, Bone, AlertTriangle, Upload,
} from 'lucide-react';
import WorkoutPlayer from './WorkoutPlayer';
import PortraitCropModal from './PortraitCropModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GOALS = [
  { id: 'blandat', label: '💪 Blandat helkroppspass' },
  { id: 'styrka', label: '🏋️ Styrka' },
  { id: 'kondition', label: '🏃 Kondition / HIIT' },
  { id: 'rorlighet', label: '🧘 Rörlighet / yoga' },
  { id: 'rumpa_ben', label: '🍑 Rumpa & ben' },
  { id: 'armar', label: '💪 Armar & axlar' },
  { id: 'core', label: '🦴 Mage / core' },
];
const EQUIP = [
  { id: 'ingen', label: 'Ingen utrustning' },
  { id: 'hantlar', label: 'Hantlar hemma' },
  { id: 'gym', label: 'Gym' },
];
const LEVELS = [
  { id: 'nybörjare', label: 'Nybörjare' },
  { id: 'medel', label: 'Medel' },
  { id: 'avancerad', label: 'Avancerad' },
];
const VIBES = [
  { id: 'warm', label: '🌸 Mjuk men bestämd' },
  { id: 'fierce', label: '🔥 Kaxig stridis' },
  { id: 'techy', label: '⚡ Cool & techy' },
  { id: 'crossfit', label: '💥 Crossfit-energi' },
];

function ExerciseCard({ ex, idx }) {
  const [img, setImg] = useState(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const loadImg = async () => {
    if (img || loading) return;
    setLoading(true);
    try {
      const r = await axios.post(`${API}/pt/exercise-image`, {
        name: ex.name, description: ex.how_to || '',
      }, { withCredentials: true });
      setImg(r.data);
    } catch (e) {
      toast.error('Kunde inte hämta bild');
    } finally { setLoading(false); }
  };

  return (
    <div className={`bg-white rounded-2xl shadow-sm border-2 transition ${done ? 'border-emerald-300 bg-emerald-50' : 'border-amber-100'} p-3`} data-testid={`pt-ex-${idx}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="w-6 h-6 rounded-full bg-amber-100 text-amber-700 text-xs font-extrabold flex items-center justify-center">{idx + 1}</span>
            <h4 className={`font-extrabold text-sm ${done ? 'text-emerald-700 line-through' : 'text-amber-900'}`}>{ex.name}</h4>
          </div>
          <div className="text-[11px] text-gray-600 mt-1">
            {ex.sets && <span className="font-bold">{ex.sets} set</span>}
            {ex.reps && <span> · {ex.reps} reps</span>}
            {ex.rest_sec && <span> · vila {ex.rest_sec}s</span>}
          </div>
        </div>
        <div className="flex gap-1">
          <button
            onClick={loadImg}
            disabled={loading}
            data-testid={`pt-ex-img-${idx}`}
            className="p-1.5 rounded-lg bg-amber-100 hover:bg-amber-200 text-amber-700 disabled:opacity-50"
            title="Visa övning"
          >
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImgIcon className="w-3.5 h-3.5" />}
          </button>
          <button
            onClick={() => setDone(!done)}
            data-testid={`pt-ex-done-${idx}`}
            className={`p-1.5 rounded-lg ${done ? 'bg-emerald-200 text-emerald-800' : 'bg-gray-100 text-gray-500 hover:bg-emerald-100 hover:text-emerald-700'}`}
            title="Klar"
          >
            <CheckCircle2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
      {img && (
        <img
          src={`data:${img.mime || 'image/png'};base64,${img.b64}`}
          alt={ex.name}
          className="w-full max-h-64 object-cover rounded-xl mt-2 border border-amber-200"
        />
      )}
      {ex.how_to && (
        <p className="text-xs text-gray-700 mt-2 whitespace-pre-wrap leading-snug">
          {ex.how_to}
        </p>
      )}
      {ex.tip && (
        <p className="text-[11px] text-amber-800 mt-1.5 italic bg-amber-50 rounded-lg px-2 py-1 border border-amber-100">
          💡 {ex.tip}
        </p>
      )}
      {Array.isArray(ex.cues) && ex.cues.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {ex.cues.map((c, i) => (
            <span key={i} className="text-[10px] bg-rose-100 text-rose-700 px-1.5 py-0.5 rounded-full font-bold">{c}</span>
          ))}
        </div>
      )}
    </div>
  );
}

export default function PTPage() {
  const [profile, setProfile] = useState(null);
  const [workouts, setWorkouts] = useState([]);
  const [stats, setStats] = useState({ total: 0, completed: 0, this_week: 0, streak: 0 });
  const [goal, setGoal] = useState('blandat');
  const [duration, setDuration] = useState(20);
  const [equipment, setEquipment] = useState('ingen');
  const [level, setLevel] = useState('nybörjare');
  const [focus, setFocus] = useState('');
  const [busy, setBusy] = useState(false);
  const [pep, setPep] = useState('');
  const [active, setActive] = useState(null);  // expanded workout
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [draft, setDraft] = useState(null);
  const [portraitBusy, setPortraitBusy] = useState(false);
  const [playerOpen, setPlayerOpen] = useState(false);
  const [releaseOpen, setReleaseOpen] = useState(false);
  const [releaseForm, setReleaseForm] = useState({ area: 'rygg', intensity: 'mjuk', duration_min: 6, notes: '' });
  const [releaseBusy, setReleaseBusy] = useState(false);
  const [cropFile, setCropFile] = useState(null);

  const loadAll = useCallback(async () => {
    try {
      const [p, w, s] = await Promise.all([
        axios.get(`${API}/pt/profile`, { withCredentials: true }),
        axios.get(`${API}/pt/workouts`, { withCredentials: true }),
        axios.get(`${API}/pt/stats`, { withCredentials: true }),
      ]);
      setProfile(p.data);
      setWorkouts(w.data?.items || []);
      setStats(s.data || {});
    } catch (e) {
      toast.error('Kunde inte ladda PT');
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Listen for global event from dashboard nudge → auto-open release modal
  useEffect(() => {
    const handler = () => setReleaseOpen(true);
    window.addEventListener('open-pt-release', handler);
    return () => window.removeEventListener('open-pt-release', handler);
  }, []);

  const generate = async () => {
    setBusy(true); setActive(null);
    try {
      const r = await axios.post(`${API}/pt/workout`, {
        goal, duration_min: Number(duration), equipment, level, focus,
      }, { withCredentials: true });
      setActive(r.data);
      await loadAll();
      toast.success(`Pass klart 💪 ${r.data.name}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Kunde inte skapa pass');
    } finally { setBusy(false); }
  };

  const fetchPep = async () => {
    try {
      const r = await axios.post(`${API}/pt/pep`, { context: 'innan passet' }, { withCredentials: true });
      setPep(r.data?.text || '');
    } catch {/**/}
  };

  const completeWorkout = async (id) => {
    try {
      await axios.post(`${API}/pt/workouts/${id}/complete`, {}, { withCredentials: true });
      toast.success('🏆 Snyggt! Loggat.');
      await loadAll();
    } catch {/**/}
  };

  const deleteWorkout = async (id) => {
    if (!window.confirm('Ta bort pass?')) return;
    try {
      await axios.delete(`${API}/pt/workouts/${id}`, { withCredentials: true });
      await loadAll();
    } catch {/**/}
  };

  const openSettings = () => {
    setDraft({ ...(profile || {}) });
    setSettingsOpen(true);
  };

  const saveSettings = async () => {
    try {
      const r = await axios.post(`${API}/pt/profile`, draft, { withCredentials: true });
      setProfile({ ...profile, ...r.data });
      toast.success('Sparat 💪');
      setSettingsOpen(false);
    } catch {
      toast.error('Kunde inte spara');
    }
  };

  const generateRelease = async () => {
    setReleaseBusy(true);
    try {
      const r = await axios.post(`${API}/pt/release-routine`, {
        area: releaseForm.area,
        intensity: releaseForm.intensity,
        duration_min: Number(releaseForm.duration_min) || 6,
        notes: releaseForm.notes || '',
      }, { withCredentials: true });
      setActive(r.data);
      setReleaseOpen(false);
      setPlayerOpen(true);
      await loadAll();
      toast.success(`Rutin klar 🦴 ${r.data.name || ''}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Kunde inte skapa rutin');
    } finally { setReleaseBusy(false); }
  };

  const genPortrait = async () => {
    setPortraitBusy(true);
    try {
      await axios.post(`${API}/pt/profile`, draft, { withCredentials: true });
      const r = await axios.post(`${API}/pt/portrait`, {}, { withCredentials: true });
      setDraft({ ...draft, portrait_b64: r.data?.portrait_b64, portrait_mime: r.data?.portrait_mime });
      setProfile({ ...profile, portrait_b64: r.data?.portrait_b64, portrait_mime: r.data?.portrait_mime });
      toast.success('Porträtt klart 💪');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Kunde inte skapa porträtt');
    } finally { setPortraitBusy(false); }
  };

  const uploadPortrait = (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    setCropFile(f);
  };

  const commitPortrait = async (blob) => {
    setPortraitBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', new File([blob], 'portrait.jpg', { type: 'image/jpeg' }));
      const r = await axios.post(`${API}/pt/portrait/upload`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setDraft({ ...draft, portrait_b64: r.data?.portrait_b64, portrait_mime: r.data?.portrait_mime });
      setProfile({ ...profile, portrait_b64: r.data?.portrait_b64, portrait_mime: r.data?.portrait_mime });
      toast.success('Bilden uppladdad 💪');
      setCropFile(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Kunde inte ladda upp');
    } finally { setPortraitBusy(false); }
  };

  if (!profile) return <div className="p-6 text-center text-amber-400"><Loader2 className="w-5 h-5 animate-spin inline" /></div>;

  const name = profile.name || 'Nova';
  const expandedExercises = active?.exercises || [];

  return (
    <div className="space-y-4" data-testid="pt-page">
      <style>{`
        @keyframes ptPulse { 0%,100%{transform:scale(1)} 50%{transform:scale(1.04)} }
        .pt-pulse { animation: ptPulse 2.4s ease-in-out infinite; }
      `}</style>

      {/* Hero with portrait + stats */}
      <div className="bg-gradient-to-br from-amber-200 via-orange-200 to-rose-200 rounded-3xl p-4 sm:p-5 shadow-sm border-2 border-amber-300">
        <div className="flex items-center gap-4">
          <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-3xl bg-white overflow-hidden border-4 border-white shadow-lg pt-pulse flex items-center justify-center flex-shrink-0">
            {profile.portrait_b64 ? (
              <img src={`data:${profile.portrait_mime || 'image/png'};base64,${profile.portrait_b64}`} alt={name} className="w-full h-full object-cover" />
            ) : (
              <Dumbbell className="w-10 h-10 text-amber-500" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <div>
                <div className="text-[10px] uppercase font-extrabold text-amber-700 tracking-widest">Din coach</div>
                <h2 className="text-2xl font-extrabold text-amber-900 leading-tight">{name}</h2>
              </div>
              <button
                onClick={openSettings}
                data-testid="pt-settings-btn"
                className="p-2 rounded-full bg-white/70 hover:bg-white text-amber-700"
              >
                <Settings className="w-4 h-4" />
              </button>
            </div>
            <div className="flex gap-2 mt-2 text-[11px] font-bold flex-wrap">
              <span className="bg-white/70 text-amber-800 px-2 py-0.5 rounded-full flex items-center gap-1" data-testid="pt-stat-streak">
                <Flame className="w-3 h-3" /> {stats.streak} dagars streak
              </span>
              <span className="bg-white/70 text-amber-800 px-2 py-0.5 rounded-full flex items-center gap-1" data-testid="pt-stat-week">
                <Trophy className="w-3 h-3" /> {stats.this_week} pass denna vecka
              </span>
              <span className="bg-white/70 text-amber-800 px-2 py-0.5 rounded-full flex items-center gap-1" data-testid="pt-stat-total">
                <Heart className="w-3 h-3" /> {stats.completed} totalt
              </span>
            </div>
          </div>
        </div>
        {pep && (
          <p className="mt-3 px-3 py-2 bg-white/70 rounded-2xl text-sm text-amber-900 italic font-medium" data-testid="pt-peptalk">
            "{pep}" — {name}
          </p>
        )}
        <button
          onClick={fetchPep}
          data-testid="pt-pep-btn"
          className="mt-2 text-xs text-amber-800 underline hover:text-amber-900 font-bold"
        >
          ✨ Pepp mig en gång till
        </button>
      </div>

      {/* Generator */}
      <div className="bg-white rounded-2xl shadow-sm border border-amber-200 p-4 space-y-3" data-testid="pt-generator">
        <h3 className="text-sm font-extrabold text-amber-700 uppercase tracking-wide flex items-center gap-1.5">
          <Sparkles className="w-4 h-4" /> Vad vill du köra idag?
        </h3>
        <div className="space-y-1">
          <label className="text-xs font-bold text-gray-700">Fokus</label>
          <div className="flex flex-wrap gap-1.5">
            {GOALS.map((g) => (
              <button
                key={g.id}
                onClick={() => setGoal(g.id)}
                data-testid={`pt-goal-${g.id}`}
                className={`px-2.5 py-1 rounded-full text-xs font-bold ${goal === g.id ? 'bg-amber-500 text-white' : 'bg-white border border-amber-200 text-amber-800'}`}
              >{g.label}</button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <div className="space-y-1">
            <label className="text-xs font-bold text-gray-700">Längd</label>
            <select value={duration} onChange={(e) => setDuration(Number(e.target.value))} data-testid="pt-duration" className="w-full px-2 py-1.5 rounded-lg border border-amber-200 text-sm bg-white">
              {[10, 15, 20, 30, 45, 60].map((m) => <option key={m} value={m}>{m} min</option>)}
            </select>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-bold text-gray-700">Utrustning</label>
            <select value={equipment} onChange={(e) => setEquipment(e.target.value)} data-testid="pt-equipment" className="w-full px-2 py-1.5 rounded-lg border border-amber-200 text-sm bg-white">
              {EQUIP.map((e) => <option key={e.id} value={e.id}>{e.label}</option>)}
            </select>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-bold text-gray-700">Nivå</label>
            <select value={level} onChange={(e) => setLevel(e.target.value)} data-testid="pt-level" className="w-full px-2 py-1.5 rounded-lg border border-amber-200 text-sm bg-white">
              {LEVELS.map((l) => <option key={l.id} value={l.id}>{l.label}</option>)}
            </select>
          </div>
        </div>
        <input
          value={focus}
          onChange={(e) => setFocus(e.target.value)}
          data-testid="pt-focus"
          placeholder="Något att veta? (t.ex. ont i knäet, ingen plankan)"
          className="w-full px-3 py-2 rounded-xl border border-amber-200 focus:border-amber-500 outline-none text-sm"
        />
        <button
          onClick={generate}
          disabled={busy}
          data-testid="pt-generate"
          className="w-full py-3 rounded-2xl bg-gradient-to-r from-amber-500 via-orange-500 to-rose-500 hover:from-amber-600 hover:via-orange-600 hover:to-rose-600 text-white font-extrabold text-sm shadow disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Flame className="w-4 h-4" />}
          {busy ? `${name} bygger ditt pass…` : `Bygg passet med ${name}`}
        </button>
        <button
          onClick={() => setReleaseOpen(true)}
          data-testid="pt-release-btn"
          className="w-full py-2.5 rounded-2xl bg-gradient-to-r from-sky-400 via-cyan-400 to-teal-400 hover:from-sky-500 hover:via-cyan-500 hover:to-teal-500 text-white font-extrabold text-sm shadow flex items-center justify-center gap-2"
        >
          <Bone className="w-4 h-4" /> Lös låsning — {name} guidar mjukt 🦴
        </button>
      </div>

      {/* Active workout */}
      {active && (
        <div className="bg-gradient-to-br from-white to-amber-50 rounded-3xl shadow-md border-2 border-amber-300 p-4 space-y-3" data-testid="pt-active">
          <div className="flex items-start justify-between gap-2">
            <div>
              <h3 className="text-lg font-extrabold text-amber-900">{active.name}</h3>
              <p className="text-xs text-amber-700 italic">{active.intro}</p>
            </div>
            <button onClick={() => setActive(null)} data-testid="pt-close-active" className="text-amber-700 hover:text-amber-900 text-xl">×</button>
          </div>
          {active.disclaimer && (
            <div className="text-[11px] bg-sky-50 text-sky-900 px-3 py-2 rounded-xl border border-sky-200 flex gap-2 items-start" data-testid="pt-release-disclaimer">
              <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
              <span>{active.disclaimer}</span>
            </div>
          )}
          <div className="space-y-2">
            {expandedExercises.map((ex, i) => <ExerciseCard key={i} ex={ex} idx={i} />)}
          </div>
          {active.outro && (
            <p className="text-xs text-amber-800 italic px-3 py-2 bg-amber-50 rounded-xl border border-amber-200">
              {active.outro}
            </p>
          )}
          <div className="flex gap-2">
            <button
              onClick={() => setPlayerOpen(true)}
              data-testid="pt-play"
              className="flex-1 py-2.5 rounded-2xl bg-gradient-to-r from-amber-500 via-orange-500 to-rose-500 hover:from-amber-600 hover:via-orange-600 hover:to-rose-600 text-white font-extrabold text-sm shadow flex items-center justify-center gap-1.5"
            >
              <Play className="w-4 h-4 fill-white" /> Starta passet — {name} guidar dig 🔥
            </button>
            <button
              onClick={() => completeWorkout(active.id)}
              data-testid="pt-complete"
              className="px-4 py-2.5 rounded-2xl bg-emerald-500 hover:bg-emerald-600 text-white font-extrabold text-sm shadow flex items-center justify-center gap-1.5"
              title="Markera klar utan att spela"
            >
              <CheckCircle2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Saved list */}
      <div className="bg-white rounded-2xl border border-amber-100 p-4 space-y-2" data-testid="pt-history">
        <h3 className="text-xs font-extrabold uppercase text-gray-600 tracking-wide">Tidigare pass</h3>
        {workouts.length === 0 && <p className="text-xs text-gray-400 italic text-center py-2">Inga pass än 💪</p>}
        {workouts.map((w) => (
          <div key={w.id} className="flex items-center justify-between gap-2 p-2 rounded-xl bg-amber-50/50 border border-amber-100" data-testid={`pt-row-${w.id}`}>
            <button onClick={() => setActive(w)} className="flex-1 text-left min-w-0">
              <div className="text-sm font-bold text-amber-900 truncate">{w.name || w.goal}</div>
              <div className="text-[10px] text-amber-700">
                {w.duration_min} min · {w.level}{w.completed_at ? ' · ✅ klart' : ''}
              </div>
            </button>
            {!w.completed_at && (
              <button onClick={() => completeWorkout(w.id)} data-testid={`pt-row-done-${w.id}`} className="text-emerald-600 hover:text-emerald-700 p-1.5" title="Markera klart">
                <CheckCircle2 className="w-3.5 h-3.5" />
              </button>
            )}
            <button onClick={() => deleteWorkout(w.id)} data-testid={`pt-row-del-${w.id}`} className="text-rose-500 hover:text-rose-700 p-1.5"><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        ))}
      </div>

      {/* Workout player (guided playthrough) */}
      {playerOpen && active && (
        <WorkoutPlayer
          workout={active}
          coachName={name}
          onClose={() => setPlayerOpen(false)}
          onComplete={async (id) => {
            await completeWorkout(id);
            setPlayerOpen(false);
          }}
        />
      )}

      {/* Release routine modal */}
      {releaseOpen && (
        <div className="fixed inset-0 z-[80] bg-black/60 backdrop-blur flex items-center justify-center p-4" onClick={() => !releaseBusy && setReleaseOpen(false)}>
          <div className="w-full max-w-md bg-white rounded-3xl shadow-2xl border-2 border-sky-200 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-sky-400 via-cyan-400 to-teal-400 px-5 py-3 flex items-center justify-between text-white">
              <h3 className="font-extrabold flex items-center gap-2"><Bone className="w-4 h-4" /> Lös låsning</h3>
              <button onClick={() => !releaseBusy && setReleaseOpen(false)} data-testid="pt-release-close" className="text-white text-2xl leading-none"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-3">
              <div className="text-[11px] bg-amber-50 text-amber-900 px-3 py-2 rounded-xl border border-amber-200 flex gap-2 items-start">
                <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                <span>Detta är <b>mjuk självmobilisering</b> — ingen knäck-manöver. Vid skarp smärta, domningar eller känselbortfall — stoppa och ring 1177.</span>
              </div>
              <div>
                <label className="text-xs font-bold text-sky-800">Var sitter låsningen?</label>
                <div className="grid grid-cols-3 gap-1.5 mt-1">
                  {[
                    { id: 'rygg', label: '🦴 Rygg' },
                    { id: 'nacke', label: '🌀 Nacke' },
                    { id: 'axlar', label: '🪽 Axlar' },
                    { id: 'höft', label: '🦵 Höft' },
                    { id: 'käke', label: '😬 Käke' },
                    { id: 'handleder', label: '✋ Handled' },
                    { id: 'helkropp', label: '🌸 Helkropp' },
                  ].map((a) => (
                    <button
                      key={a.id}
                      onClick={() => setReleaseForm({ ...releaseForm, area: a.id })}
                      data-testid={`pt-release-area-${a.id}`}
                      className={`px-2 py-1.5 rounded-xl text-xs font-bold border-2 ${releaseForm.area === a.id ? 'bg-sky-500 border-sky-600 text-white' : 'bg-white border-sky-200 text-sky-800'}`}
                    >{a.label}</button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs font-bold text-sky-800">Intensitet</label>
                  <select value={releaseForm.intensity} onChange={(e) => setReleaseForm({ ...releaseForm, intensity: e.target.value })} data-testid="pt-release-intensity" className="w-full px-2 py-1.5 rounded-lg border border-sky-200 text-sm bg-white">
                    <option value="mjuk">Mjuk</option>
                    <option value="medel">Medel</option>
                    <option value="djup">Djup</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-bold text-sky-800">Längd</label>
                  <select value={releaseForm.duration_min} onChange={(e) => setReleaseForm({ ...releaseForm, duration_min: Number(e.target.value) })} data-testid="pt-release-duration" className="w-full px-2 py-1.5 rounded-lg border border-sky-200 text-sm bg-white">
                    {[3, 5, 6, 8, 10, 12, 15].map((m) => <option key={m} value={m}>{m} min</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs font-bold text-sky-800">Detaljer (frivilligt)</label>
                <input
                  value={releaseForm.notes}
                  onChange={(e) => setReleaseForm({ ...releaseForm, notes: e.target.value })}
                  placeholder="t.ex. ont vid framåtböj, stelt åt vänster"
                  data-testid="pt-release-notes"
                  className="w-full px-3 py-2 rounded-xl border border-sky-200 focus:border-sky-500 outline-none text-sm"
                />
              </div>
              <button
                onClick={generateRelease}
                disabled={releaseBusy || !releaseForm.area}
                data-testid="pt-release-generate"
                className="w-full py-2.5 rounded-2xl bg-gradient-to-r from-sky-500 via-cyan-500 to-teal-500 text-white font-extrabold text-sm shadow disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {releaseBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bone className="w-4 h-4" />}
                {releaseBusy ? `${name} förbereder rutinen…` : `Bygg rutin & starta med ${name}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Settings modal */}
      {settingsOpen && draft && (
        <div className="fixed inset-0 z-[80] bg-black/60 backdrop-blur flex items-center justify-center p-4" onClick={() => setSettingsOpen(false)}>
          <div className="w-full max-w-md bg-white rounded-3xl shadow-2xl border-2 border-amber-200 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-amber-400 to-orange-500 px-5 py-3 flex items-center justify-between text-white">
              <h3 className="font-extrabold flex items-center gap-2"><Dumbbell className="w-4 h-4" /> Din PT</h3>
              <button onClick={() => setSettingsOpen(false)} className="text-white text-2xl leading-none"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <label className="text-xs font-bold text-amber-700">Namn</label>
                <input value={draft.name || ''} onChange={(e) => setDraft({ ...draft, name: e.target.value })} data-testid="pt-name-input" className="w-full px-3 py-2 rounded-xl border border-amber-200 text-sm" placeholder="Nova" />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-bold text-amber-700">Vibe</label>
                <div className="grid grid-cols-1 gap-1.5">
                  {VIBES.map((v) => (
                    <button
                      key={v.id}
                      onClick={() => setDraft({ ...draft, vibe: v.id })}
                      data-testid={`pt-vibe-${v.id}`}
                      className={`text-left px-3 py-2 rounded-xl text-sm border-2 ${draft.vibe === v.id ? 'bg-amber-100 border-amber-400 text-amber-900' : 'bg-white border-gray-200 hover:border-amber-200'}`}
                    >{v.label}</button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div><label className="text-[11px] font-bold text-amber-700">Hår</label>
                  <input value={draft.hair || ''} onChange={(e) => setDraft({ ...draft, hair: e.target.value })} data-testid="pt-hair" className="w-full px-2 py-1.5 rounded-lg border border-amber-200 text-xs" placeholder="t.ex. blont hög ponny" />
                </div>
                <div><label className="text-[11px] font-bold text-amber-700">Ögon</label>
                  <input value={draft.eyes || ''} onChange={(e) => setDraft({ ...draft, eyes: e.target.value })} data-testid="pt-eyes" className="w-full px-2 py-1.5 rounded-lg border border-amber-200 text-xs" placeholder="t.ex. skarpa gröna" />
                </div>
                <div className="col-span-2"><label className="text-[11px] font-bold text-amber-700">Kropp</label>
                  <input value={draft.body || ''} onChange={(e) => setDraft({ ...draft, body: e.target.value })} data-testid="pt-body" className="w-full px-2 py-1.5 rounded-lg border border-amber-200 text-xs" placeholder="t.ex. atletisk, stark, lång" />
                </div>
                <div className="col-span-2"><label className="text-[11px] font-bold text-amber-700">Outfit</label>
                  <input value={draft.style || ''} onChange={(e) => setDraft({ ...draft, style: e.target.value })} data-testid="pt-style" className="w-full px-2 py-1.5 rounded-lg border border-amber-200 text-xs" placeholder="t.ex. svart sportbh + tights" />
                </div>
                <div className="col-span-2"><label className="text-[11px] font-bold text-amber-700">Extra detaljer</label>
                  <input value={draft.appearance_extra || ''} onChange={(e) => setDraft({ ...draft, appearance_extra: e.target.value })} data-testid="pt-extra" className="w-full px-2 py-1.5 rounded-lg border border-amber-200 text-xs" placeholder="t.ex. solbränd, tatuering på axeln" />
                </div>
              </div>
              <div className="flex gap-2 items-center">
                {draft.portrait_b64 && (
                  <img src={`data:${draft.portrait_mime || 'image/png'};base64,${draft.portrait_b64}`} alt="" className="w-16 h-16 rounded-2xl object-cover border-2 border-amber-200" />
                )}
                <button
                  onClick={genPortrait}
                  disabled={portraitBusy}
                  data-testid="pt-portrait-btn"
                  className="flex-1 py-2 rounded-xl bg-gradient-to-r from-amber-400 to-orange-400 text-white text-xs font-bold disabled:opacity-50 flex items-center justify-center gap-1.5"
                >
                  {portraitBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                  {draft.portrait_b64 ? 'Nytt porträtt' : 'Skapa porträtt ✨'}
                </button>
                <label
                  data-testid="pt-portrait-upload-label"
                  className={`py-2 px-3 rounded-xl bg-white border-2 border-amber-300 text-amber-700 text-xs font-bold flex items-center gap-1 cursor-pointer hover:bg-amber-50 ${portraitBusy ? 'opacity-50 pointer-events-none' : ''}`}
                  title="Ladda upp egen bild"
                >
                  <Upload className="w-3.5 h-3.5" />
                  Ladda upp
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={uploadPortrait}
                    disabled={portraitBusy}
                    data-testid="pt-portrait-upload"
                    className="hidden"
                  />
                </label>
              </div>
              <button onClick={saveSettings} data-testid="pt-save" className="w-full py-2.5 rounded-xl bg-amber-500 hover:bg-amber-600 text-white font-bold text-sm">Spara</button>
            </div>
          </div>
        </div>
      )}

      {cropFile && (
        <PortraitCropModal
          file={cropFile}
          title={`Beskär ${profile?.name || 'Nova'}-bild`}
          accent="amber"
          onCancel={() => setCropFile(null)}
          onConfirm={commitPortrait}
        />
      )}
    </div>
  );
}
